import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import type { AboutInfo } from "@/hooks/usePublicAbout";

interface Props { about: AboutInfo | null; }

export default function StorySection({ about }: Props) {
  const { language } = useLanguage();
  const { localizedTagline } = useBrand();

  const story = language === "ar" ? about?.storyAr || about?.story : about?.story;
  const mission = language === "ar" ? about?.missionAr || about?.mission : about?.mission;

  const stats = [
    { value: "4.5★", label: language === "ar" ? "تقييم العملاء" : "Customer Rating" },
    { value: "50+", label: language === "ar" ? "صنف متنوع" : "Menu Items" },
    { value: "100K+", label: language === "ar" ? "عميل سعيد" : "Happy Customers" },
    { value: "50+", label: language === "ar" ? "سنة خبرة" : "Years" },
  ];

  return (
    <section id="story-section" className="py-20 md:py-28 bg-card">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Text side */}
          <div className="space-y-6">
            <h2 className="font-heading text-4xl md:text-6xl font-black text-foreground leading-tight">
              {language === "ar" ? "قصتنا" : "Our Story"}
            </h2>
            <p className="text-primary font-bold text-sm uppercase tracking-widest">
              {localizedTagline(language)}
            </p>
            <div className="space-y-5 text-muted-foreground leading-[1.9] text-base md:text-lg">
              {story && <p>{story}</p>}
              {mission && <p className="italic text-foreground/60">"{mission}"</p>}
              {!story && !mission && (
                <p>
                  {language === "ar"
                    ? "نحن نقدم تجربة طعام فريدة من نوعها تجمع بين الأصالة والجودة العالية، مع أفضل المكونات وأعرق الوصفات."
                    : "We offer a unique dining experience that combines authenticity and premium quality with the finest ingredients and time-honored recipes."}
                </p>
              )}
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-border bg-background p-6 text-center hover:border-primary/40 transition-colors group"
              >
                <span className="block font-heading text-3xl md:text-4xl font-black text-primary group-hover:scale-105 transition-transform inline-block">
                  {stat.value}
                </span>
                <span className="text-sm text-muted-foreground mt-2 block">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
